import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    TextField,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SaveIcon from "@mui/icons-material/Save";
import { NOMBRE_COLA_MAX } from "../../constants";

const validarNombre = (nombre) => {
    if (!nombre) return "El nombre es obligatorio";
    if (nombre.length > NOMBRE_COLA_MAX) return `Máximo ${NOMBRE_COLA_MAX} caracteres`;
    return "";
};

/**
 * Modal de alta y edición de una cola (único campo: nombre).
 *
 * No hace llamadas HTTP: `onGuardar(nombre)` es asíncrono y lo gestiona la
 * página. Si rechaza, el modal permanece abierto con el valor escrito.
 */
export default function ModalCola({ show, onHide, onGuardar, cola = null, guardando = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [nombre, setNombre] = useState("");
    const [tocado, setTocado] = useState(false);

    // Reinicia el estado cada vez que se abre, desde la cola recibida
    useEffect(() => {
        if (show) {
            setNombre(cola?.nombre ?? "");
            setTocado(false);
        }
    }, [show, cola]);

    const nombreLimpio = nombre.trim();
    const errorNombre = validarNombre(nombreLimpio);
    const mostrarError = tocado && Boolean(errorNombre);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTocado(true);
        if (errorNombre || guardando) return;
        try {
            await onGuardar(nombreLimpio);
        } catch (err) {
            // El error lo muestra la página como toast; el modal se queda abierto
        }
    };

    return (
        <Dialog
            open={show}
            onClose={guardando ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                component: "form",
                onSubmit: handleSubmit,
                noValidate: true,
                sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <ListAltIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                        {cola ? "Editar cola" : "Nueva cola"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={guardando} size="small" aria-label="Cerrar">
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}
            >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "#495057" }}>
                    Nombre *
                </Typography>
                <TextField
                    fullWidth
                    autoFocus
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    onBlur={() => setTocado(true)}
                    placeholder="Nombre de la cola"
                    disabled={guardando}
                    error={mostrarError}
                    helperText={mostrarError ? errorNombre : " "}
                    inputProps={{ "aria-label": "Nombre de la cola" }}
                />
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}>
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={guardando}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#6c757d", borderColor: "#6c757d", py: { xs: 1.25, sm: 1 } }}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth={isMobile}
                    disabled={guardando}
                    startIcon={guardando ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.25, sm: 1 },
                        backgroundColor: "#cc071e",
                        "&:hover": { backgroundColor: "#a30618" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {guardando ? "Guardando..." : "Guardar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
